import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiCalendar, FiArrowLeft, FiClock } from "react-icons/fi";
import blogServices from '../services/BlogServices';

function PostViewPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        blogServices.getPost(slug)
            .then(res => {
                setPost(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.log(err);
                setError(true);
                setLoading(false);
            });
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-base-100">
                <section data-theme="guardhartDark" className="relative h-[400px] md:h-[500px] bg-neutral">
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral via-neutral/60 to-neutral/30"></div>
                </section>
                <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 animate-pulse">
                    <div className="bg-zinc-200 h-4 w-32 rounded mb-6"></div>
                    <div className="bg-zinc-200 h-10 w-3/4 rounded mb-4"></div>
                    <div className="bg-zinc-200 h-10 w-1/2 rounded mb-8"></div>
                    <div className="bg-zinc-200 h-4 w-full rounded mb-2"></div>
                    <div className="bg-zinc-200 h-4 w-full rounded mb-2"></div>
                    <div className="bg-zinc-200 h-4 w-2/3 rounded"></div>
                </section>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-base-100 flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <h2 className="text-2xl font-bold text-base-content mb-3">Post not found</h2>
                    <p className="text-zinc-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
                    <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:underline">
                        <FiArrowLeft className="w-4 h-4" />
                        Back to all posts
                    </Link>
                </div>
            </div>
        );
    }

    const readingTime = Math.max(1, Math.ceil(post.content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200));

    return (
        <div className="min-h-screen bg-base-100">

            {/* Hero Section (Dark Theme) */}
            <section data-theme="guardhartDark" className="relative overflow-hidden border-b border-zinc-800">
                {/* Background Image */}
                {post.image && (
                    <div className="absolute inset-0 z-0">
                        <div
                            className="w-full h-full bg-cover bg-center"
                            style={{ backgroundImage: `url(${post.image})` }}
                        ></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-neutral via-neutral/70 to-neutral/40"></div>
                    </div>
                )}

                {/* Content */}
                <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-primary transition-colors mb-8"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back to all posts
                    </Link>

                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-base-content mb-6 leading-tight">
                        {post.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1.5">
                            <FiCalendar className="w-4 h-4 text-primary" />
                            {new Date(post.created_at).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                            })}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-zinc-600"></span>
                        <span className="flex items-center gap-1.5">
                            <FiClock className="w-4 h-4 text-primary" />
                            {readingTime} min read
                        </span>
                    </div>
                </div>
            </section>

            {/* Article Content (Light Theme) */}
            <article className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <div
                    className="prose prose-zinc prose-lg max-w-none
                               prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-base-content
                               prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl
                               prose-h2:mt-12 prose-h2:mb-6
                               prose-p:text-zinc-600 prose-p:leading-relaxed prose-p:mb-6
                               prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                               prose-strong:text-base-content prose-strong:font-semibold
                               prose-ul:text-zinc-600 prose-ol:text-zinc-600
                               prose-li:marker:text-primary
                               prose-blockquote:border-l-primary prose-blockquote:bg-base-200 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r prose-blockquote:not-italic prose-blockquote:text-zinc-700
                               prose-img:rounded-lg prose-img:border prose-img:border-zinc-200 prose-img:shadow-sm
                               prose-hr:border-zinc-200"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />
                <div className="mt-16 pt-10 border-t border-zinc-200">
                    <div className="bg-base-200 border border-zinc-200 rounded-lg p-8 md:p-10 text-center">
                        <h3 className="text-2xl font-bold text-base-content mb-3">
                            Need expert security advice?
                        </h3>
                        <p className="text-zinc-600 mb-6 max-w-lg mx-auto">
                            Our team is ready to help you design a security solution tailored to your needs.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 bg-primary text-primary-content px-8 py-3 text-xs font-bold uppercase tracking-widest rounded hover:brightness-110 transition-all active:scale-95"
                        >
                            Get in Touch
                        </Link>
                    </div>
                </div>
            </article>

        </div>
    );
}

export default PostViewPage;